import type { ParsedBody, FileMetadata } from '../types/parsed-body';

type BodyParser = (raw: string) => ParsedBody;

const parsers = new Map<string, BodyParser>();

function flattenObject(
  obj: unknown,
  prefix = '',
  fields: Record<string, string> = {},
  depth = 0,
): Record<string, string> {
  if (depth > 10) return fields;
  if (obj === null || obj === undefined) return fields;
  if (typeof obj !== 'object') {
    fields[prefix] = String(obj);
    return fields;
  }
  if (Array.isArray(obj)) {
    fields[prefix] = JSON.stringify(obj);
    return fields;
  }
  for (const [key, value] of Object.entries(obj as Record<string, unknown>)) {
    const fullKey = prefix ? `${prefix}.${key}` : key;
    if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
      flattenObject(value, fullKey, fields, depth + 1);
    } else if (Array.isArray(value)) {
      fields[fullKey] = JSON.stringify(value);
    } else {
      fields[fullKey] = value === null || value === undefined ? '' : String(value);
    }
  }
  return fields;
}

// JSON parser
function parseJson(raw: string): ParsedBody {
  try {
    const parsed = JSON.parse(raw);
    const fields = flattenObject(parsed);
    return { type: 'json', fields, files: [], raw };
  } catch {
    return { type: 'json', fields: {}, files: [], raw };
  }
}

// Form-urlencoded parser
function parseFormUrlEncoded(raw: string): ParsedBody {
  const params = new URLSearchParams(raw);
  const fields: Record<string, string> = {};
  params.forEach((value, key) => {
    fields[key] = value;
  });
  return { type: 'form', fields, files: [], raw };
}

// Text parser
function parseText(raw: string): ParsedBody {
  return { type: 'text', fields: {}, files: [], raw };
}

// Binary/unknown fallback parser
function parseBinary(_raw: string): ParsedBody {
  return { type: 'binary', fields: {}, files: [], raw: null };
}

// Multipart parser — used after formData() consumption, not from raw text
export function parseMultipartFormData(formData: FormData): ParsedBody {
  const fields: Record<string, string> = {};
  const files: FileMetadata[] = [];

  formData.forEach((value, key) => {
    if (typeof value === 'string') {
      fields[key] = value;
    } else {
      // File object
      files.push({
        fieldName: key,
        fileName: value.name,
        contentType: value.type || 'application/octet-stream',
        size: value.size,
      });
    }
  });

  return { type: 'multipart', fields, files, raw: null };
}

// Register built-in parsers
parsers.set('application/json', parseJson);
parsers.set('application/x-www-form-urlencoded', parseFormUrlEncoded);
parsers.set('text/', parseText);

export function registerParser(contentType: string, parser: BodyParser): void {
  parsers.set(contentType, parser);
}

export function parseBody(contentType: string | null, raw: string): ParsedBody {
  if (!contentType) {
    return parseBinary(raw);
  }

  const ct = contentType.toLowerCase().split(';')[0].trim();

  // Exact match first
  const exactParser = parsers.get(ct);
  if (exactParser) {
    return exactParser(raw);
  }

  // Prefix match (e.g., text/* matches text/plain, text/html)
  for (const [prefix, parser] of parsers.entries()) {
    if (prefix.endsWith('/') && ct.startsWith(prefix)) {
      return parser(raw);
    }
  }

  return parseBinary(raw);
}

export function isMultipartFormData(contentType: string | null): boolean {
  if (!contentType) return false;
  return contentType.toLowerCase().includes('multipart/form-data');
}
